import React from 'react';
import { ShieldCheckIcon, CoinsIcon } from 'lucide-react';
import { TopoLines } from './TopoLines';
const blocks = [{
  time: 'Gate 1',
  name: 'Safety & Electrical',
  body: 'Interlocks, key, battery, charging and starter. The fastest checks, and the ones most often mistaken for a fuel problem.',
  dot: 'bg-patrol-mustard'
}, {
  time: 'Gate 2',
  name: 'Spark',
  body: 'Plug, coil, air gap and kill circuit — graded on the Spark Quality Scale so a weak spark doesn’t pass as a good one.',
  dot: 'bg-ember'
}, {
  time: 'Gate 3',
  name: 'Fuel & Air',
  body: 'Fuel path, vent, carb, air filter and governor — with the Carb Decision Gate: adjust, clean, rebuild or replace.',
  dot: 'bg-sky'
}, {
  time: 'Gate 4',
  name: 'Compression & Mechanical',
  body: 'Compression, valves, flywheel key and internal wear, read on the Compression Verdict Chart.',
  dot: 'bg-moss'
}, {
  time: 'Then',
  name: 'Pickup-Ready',
  body: 'Repair order, estimate, verification run and pickup notice — the job leaves the bench documented.',
  dot: 'bg-signal'
}];
export function ProductReveal() {
  return <section aria-labelledby="mechanism-heading" className="topo-dark relative w-full overflow-hidden border-y-4 border-ember bg-forest" data-id="element-262" data-mp="1f06gzzuwhv39">
      <TopoLines className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 text-paper/[0.06]" data-id="element-263" data-mp="1or12v348iso6" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-264" data-mp="s3eiw4ocl2rm">
        <div className="text-center" data-id="element-265" data-mp="w2wv8c23s5iu">
          <p className="eyebrow text-ember" data-id="element-266" data-mp="w3yjji84qoii">The mechanism</p>
          <h2 id="mechanism-heading" className="trail-underline mt-3 font-display text-[34px] uppercase leading-tight text-paper sm:text-6xl" data-id="element-267" data-mp="112i4pgewfw9t">
            The 4-Gate Diagnostic Sequence
          </h2>
          <p className="field-note mt-5 text-[28px] uppercase leading-tight text-paper sm:text-3xl" data-id="element-268" data-mp="2choo0q35veso">
            4 gates. Always the same order. Before you spend on parts.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-base text-paper/80 sm:text-lg" data-id="element-269" data-mp="1rrkual9u2bu2">
            Every small engine needs the same things to run. The method tests
            them in the same order every time — cheapest and fastest test
            first — and never moves to the next gate until the last one
            passes. Every symptom card in the pack is that same route, applied
            to that symptom, on 2- and 4-stroke engines.
          </p>
        </div>

        <ol className="relative mx-auto mt-9 max-w-2xl" data-id="element-270" data-mp="1442uc2gf07g0">
          <span aria-hidden="true" className="absolute bottom-6 left-[19px] top-6 border-l-2 border-dashed border-paper/25" data-id="element-271" data-mp="141gny659fxzd" />
          {blocks.map((block, i) => <li key={block.name} className="relative flex gap-4 pb-4 last:pb-0" data-id="element-272" data-mp="1dzmoajbf4w24" data-mpi={i}>
              <span className={`relative z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full font-display text-lg text-pine ring-4 ring-forest ${block.dot}`} data-id="element-273" data-mp="qa2642n3a2tw">
                {i < 4 ? i + 1 : '✓'}
              </span>
              <div className="flex-1 rounded-xl border border-paper/10 bg-paper/[0.06] px-4 py-3" data-id="element-274" data-mp="31rx8hbu19wjo">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3" data-id="element-275" data-mp="2n30qaly9j35c">
                  <span className="font-display text-xl uppercase text-paper" data-id="element-276" data-mp="2s0areyv0l390">
                    {block.name}
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-ember" data-id="element-277" data-mp="3frg4555nrff3">
                    {block.time}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-snug text-paper/80" data-id="element-278" data-mp="36ox6r0kn5v08">
                  {block.body}
                </p>
              </div>
            </li>)}
        </ol>

        <div className="mx-auto mt-6 grid max-w-2xl gap-3 sm:grid-cols-2" data-id="element-279" data-mp="1vajvcv72r813">
          <div className="rounded-xl bg-moss-soft px-4 py-4 text-pine" data-id="element-280" data-mp="1zcesdnhz2z92">
            <span className="flex items-center gap-2 font-display text-lg uppercase" data-id="element-281" data-mp="1zaagojdu0vy8">
              <CoinsIcon className="h-5 w-5 text-moss-deep" aria-hidden="true" data-id="element-282" data-mp="2fkuhv1dic3g9" />
              Cheapest-Test-First Rule
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-283" data-mp="25tgnt94brvui">
              Every test ordered by time and cost, so you prove the fault
              before the parts counter does.
            </p>
          </div>
          <div className="rounded-xl bg-ember-soft px-4 py-4 text-pine" data-id="element-284" data-mp="2yinke2k7ybd4">
            <span className="flex items-center gap-2 font-display text-lg uppercase" data-id="element-285" data-mp="2tjaxjhtcl4aj">
              <ShieldCheckIcon className="h-5 w-5 text-ember-deep" aria-hidden="true" data-id="element-286" data-mp="2thhs25uh0q44" />
              Confirm OEM Spec
            </span>
            <p className="mt-1.5 text-sm leading-snug text-pine-soft" data-id="element-287" data-mp="2kje5d67r1naq">
              No made-up numbers. Values come as typical ranges with the
              “Confirm OEM spec” seal, and you learn to read the model tag to
              find the official one.
            </p>
          </div>
        </div>
      </div>
    </section>;
}